/****************************************************
 * Module Name:  mixInvoiceAccount.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import Select from "react-select";
import { Box, Button, CircularProgress, Tooltip, Typography, Stack, FormGroup, FormControlLabel, Switch } from "@mui/material";
import Constants from "../../utility/constants";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Utility from "../../utility/utility";
import { DataGridPro, GridToolbar, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { ThemeProvider, createTheme } from "@mui/material";
import { BsFilePdf } from "react-icons/bs";
import { FaMoneyBill } from "react-icons/fa";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { LoadingButton } from "@mui/lab";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import PDFModal from "../common/pdfModal";
import renderCellExpand from "../common/dataGridCellExpand";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";
import MixSaleProxy from "../../utility/proxy/manager/mixProxy/mixSalesProxy";
import InvoicePaymentModal from "../report/invoicePaymentModal";
import BucketDetailModal from "./bucketDetailModal";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.tableLayouts = {
    Mylayout: {
        fillColor: function (rowIndex, node, columnIndex) {
            return rowIndex % 2 === 0 ? "#CCCCCC" : null;
        },
    },
};

const lStatusOption = [
    { value: "UNPAID", label: "UNPAID" },
    { value: ["PAID", "UNPAID"], label: "-- ANY --" },
    { value: "PAID", label: "PAID" },
];

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .super-app-theme--Paid': {
        color: 'green',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },
    '& .super-app-theme--PartiallyPaid': {
        color: 'orange',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },
    '& .super-app-theme--Unpaid': {
        color: '#000',
        fontWeight: 'normal',
        whiteSpace: 'nowrap',
    },
}));

const MixInvoiceAccount = () => {
    const local_permision = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MIX_REPORT_ACCOUNT_INVOICE);
    const access = Utility.isReadAllowed(local_permision);

    const [user, setUser] = useState([]);
    const [userOption, setUserOption] = useState([]);
    const [status, setStatus] = useState(lStatusOption[0]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [finalAmount, setFinalAmount] = useState("");
    const [invoiceid, setInvoiceid] = useState("");
    const [currency, setCurrency] = useState("");
    const [listData, setListData] = useState([]);
    const [salesListLoader, setSalesListLoader] = useState(false);
    const [mainData, setMainData] = useState([]);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showmodal, setShowModal] = useState(false);
    const [location, setLocation] = useState("");
    const [locationOptions, setLocationOptions] = useState([]);
    const [pdfName, setPdfName] = useState("");
    const [pdfByte, setPdfByte] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [showBucketDetailModal, setShowBucketDetailModal] = useState(false);
    const [bucketId, setBucketId] = useState([]);
    const [groupBy, setGroupBy] = useState(false);
    const [loader, setLoader] = useState(false);
    const [showUserDetailModal, setShowUserDetailModal] = useState(false);

    let date = new Date(); //Current Date and Time

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        PageTitle("MIX_REPORT_ACCOUNT_INVOICE");
        if (access) {
            getAllLocationData();
            setFromDate(new Date(date.setDate(date.getDate())));
            setToDate(new Date());
        }
    }, []);

    const getAllLocationData = () => {
        LocationProxy.GetAllLocationDetailMini((response) => {
            setAllLocationData(response);
        });
    };

    useEffect(() => {
        if (location.value) {
            getAllCustomerData()
        }
    }, [location]);

    const getAllCustomerData = () => {

        let locationid = location.value;
        let userid = -1; //will fetch all the customers 
        MixSaleProxy.GetCustomersCreatedInvoice(locationid, userid, Constants.loggedInUser.usertype, (response) => {
            if (response)
                setAllUserData(response);
        });
    }

    const setAllUserData = (response) => {
        let optionsArray = [];
        optionsArray.push({ value: "*", label: "-- ANY --" });
        response.map((data) => {
            let option = {
                value: data.id,
                label: data.companyname,
            };
            optionsArray.push(option);
            return true;
        });
        setUserOption(optionsArray);
        setUser(optionsArray.length > 0 ? [optionsArray[0]] : user);
        handleSearch(fromDate, toDate, [optionsArray[0]], optionsArray, location);
    };

    const setAllLocationData = (response) => {
        let optionsArray = [];
        optionsArray.push({ value: -1, label: "-- ANY --" });
        response.forEach((data) => {
            let option = { value: data.id, label: data.name };
            optionsArray.push(option);
        });
        setLocation(optionsArray[0]);
        setLocationOptions(optionsArray);
    }

    const fullOptionToValue = (selectedData, userOption) => {
        let newArr = [];
        selectedData.map((data) => {
            if (data.value === "*") {
                return userOption.map((option) => {
                    if (option.value !== "*") {
                        newArr.push(option.value);
                        return true;
                    }
                    return true;
                });
            }
            return true;
        })
        return Utility.GetValueArrayWithStarReplace(selectedData, newArr);
    };

    const handleSearch = (fromDate, toDate, user, userOption, location) => {
        if (fromDate && toDate && user && location) {
            setSalesListLoader(true);
            let data = {
                from: dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z",
                to: dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z",
                users: fullOptionToValue(user, userOption),
                status: status.label === "-- ANY --" ? status.value : [status.value],
                locationid: location.value,
            };

            MixSaleProxy.GetAllMixInvoiceReport(Constants.loggedInUser.id, Constants.loggedInUser.usertype, data, (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        customercompanyname: Utility.getDistinctOptions(response, "customercompanyname"),
                        status: Utility.getDistinctOptions(response, "status"),
                        exchangecurrency: Utility.getDistinctOptions(response, "exchangecurrency"),
                        user: Utility.getDistinctOptions(response, "userid"),
                        broker: Utility.getDistinctOptions(response, "brokerid"),
                        customer: Utility.getDistinctOptions(response, "customerid"),
                        invoicecustomer: Utility.getDistinctOptions(response, "invoicecustomerid"),
                        tradingcompany: Utility.getDistinctOptions(response, "tradingcompanyid"),
                    };
                    const combinedFilterValues = {
                        customercompanyname: uniqueFilterValues.customercompanyname,
                        status: uniqueFilterValues.status,
                        exchangecurrency: uniqueFilterValues.exchangecurrency,
                        user: uniqueFilterValues.user.map(value => {
                            const matchingItem = response.find(item => item.userid === value);
                            return matchingItem ? `${value} | ${matchingItem.username}` : [];
                        }),
                        broker: uniqueFilterValues.broker.map(value => {
                            const matchingItem = response.find(item => item.brokerid === value);
                            if (matchingItem) {
                                let displayValue = matchingItem.brokerid === -1 ? " - " : `${matchingItem.brokerid} | ${matchingItem.brokercompanyname}`;
                                return displayValue;
                            }
                            return [];
                        }),
                        customer: uniqueFilterValues.customer.map(value => {
                            const matchingItem = response.find(item => item.customerid === value);
                            return matchingItem ? `${value} | ${matchingItem.customercompanyname}` : [];
                        }),
                        invoicecustomer: uniqueFilterValues.invoicecustomer.map(value => {
                            const matchingItem = response.find(item => item.invoicecustomerid === value);
                            if (matchingItem) {
                                let displayValue = (!matchingItem.invoicecustomerid) ? " - " : `${matchingItem.invoicecustomerid} | ${matchingItem.invoicecustomercompanyname}`;
                                return displayValue;
                            }
                            return [];
                        }),
                        tradingcompany: uniqueFilterValues.tradingcompany.map(value => {
                            const matchingItem = response.find(item => item.tradingcompanyid === value);
                            return matchingItem ? `${value} | ${matchingItem.tradingcompanyname}` : [];
                        }),
                        proformaStatus: [...new Set(response.flatMap(iData => Utility.getDistinctOptions(iData.stones, 'status')))]
                    };
                    setFilterValues(combinedFilterValues);
                    setListData(response);
                    handleArray(response);
                    setSalesListLoader(false);
                } else {
                    setSalesListLoader(false);
                    setErrorSnackbar(true);
                }
            });
        }
    };

    const handlePayment = (data) => {
        setLoader(true);
        MixSaleProxy.PayMixInvoiceAmount(data, (response) => {
            if (response) {
                setLoader(false);
                setShowModal(false)
                handleSearch(fromDate, toDate, user, userOption, location);
            } else {
                setLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const handleBucketDetailModal = (params) => {
        setBucketId(params.value);
        setShowBucketDetailModal(true)
    };


    const handleValidation = () => {
        return true
    }

    const handleArray = (lListData) => {
        let newMainData = [];
        if (lListData.length > 0) {
            lListData.forEach((list) => {
                list.buckets.forEach((stone) => {
                    stone.customercompanyname = list.customercompanyname;
                    newMainData.push(stone)
                });
            });
        }
        setMainData(newMainData);
    };

    const downloadPDF = (invoiceid, fileBytes) => {
        setPdfByte(fileBytes);
        setPdfName(`Invoice` + invoiceid + `.pdf`);
        openModal()
    }

    const columnName = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "invoiceid",
            headerName: "INVOICE ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "bucketid",
            headerName: "BUCKET ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowBucketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleBucketDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "bucketname",
            headerName: "BUCKET NAME",
            headerAlign: "center",
            align: "center",
            width: 300,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.bucketname : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) =>
                params.row.stonetypefullname +
                " | " +
                params.row.shape +
                " | " +
                params.row.size +
                " | " +
                params.row.color +
                " | " +
                params.row.clarity,
            renderCell: (params) =>
                params.row.stonetypefullname +
                " | " +
                params.row.shape +
                " | " +
                params.row.size +
                " | " +
                params.row.color +
                " | " +
                params.row.clarity,
        },
        {
            field: "customercompanyname",
            headerName: "CUSTOMER COMPANY NAME",
            headerAlign: "center",
            align: "center",
            width: 220,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.customercompanyname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "qty",
            headerName: "QTY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
        },
        {
            field: "piece",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
        },
        {
            field: "sellqty",
            headerName: "SELL QTY",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 80,
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "discount1",
            headerName: "DIS 1",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
        },
        {
            field: "discount2",
            headerName: "DIS 2",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 55,
        },
        {
            field: "discount3",
            headerName: "DIS 3",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
        },
        {
            field: "final_amount",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.final_amount),
            width: 110,
        },
        {
            field: "remarks",
            headerName: "REMARKS",
            headerAlign: "center",
            align: "center",
            width: 90,
        },
    ];

    const materialDefaultColumns = [
        {
            headerName: "PAY",
            field: "pay",
            headerAlign: "center",
            export: false,
            width: 95,
            renderCell: (params) => (
                params.row.status === 'PAID' ? "Fully Paid" :
                    Utility.isAddAllowed(local_permision) || Utility.isUpdateAllowed(local_permision) ?
                        <Tooltip title="Pay">
                            <Button
                                variant="contained"
                                component="label"
                                size="small"
                                startIcon={<FaMoneyBill style={{ width: '20px', height: '20px' }} />}
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setFinalAmount(params.row.finalpayableamount - params.row.amountpaid)
                                    setInvoiceid(params.row.id)
                                    setCurrency(params.row.exchangecurrency)
                                    setShowModal(true)
                                }}
                                disabled={Constants.loggedInUser.usertype === "E" ? false : true}
                            >
                                Pay
                            </Button>
                        </Tooltip>
                        : ''
            ),
        },
        {
            headerName: "INVOICE ID",
            field: "id",
            headerAlign: "center",
            width: 115,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "STONETYPE",
            field: "stonetype",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
            width: 115,
        },
        {
            headerName: "USER",
            field: "userid",
            width: 150,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            headerAlign: "center",
            align: "center",
            valueGetter: (params) => {
                return `${params.row.userid} | ${params.row.username}`;
            },
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {`${params.row.userid} | ${params.row.username}`}
                    </span>
                </div>
            ),
        },
        {
            headerName: "BROKER COMPANY",
            field: "brokerid",
            width: 170,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.broker : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return params.row.brokerid === -1 ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`;
            },
            renderCell: (params) => {
                let displayValue = params.row.brokerid === -1 ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {(params.row.brokerid === -1 || params.row.brokerid === null) ? " - " : displayValue}
                        </span>
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "CUSTOMER COMPANY",
            field: "customerid",
            width: 225,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.customer : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return `${params.row.customerid} | ${params.row.customercompanyname}`;
            },
            renderCell: (params) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {`${params.row.customerid} | ${params.row.customercompanyname}`}
                        </span>
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "TRADING COMPANY",
            field: "tradingcompanyid",
            width: 185,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tradingcompany : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                return `${params.row.tradingcompanyid} | ${params.row.tradingcompanyname}`;
            },
            renderCell: (params) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                            {`${params.row.tradingcompanyid} | ${params.row.tradingcompanyname}`}
                        </span>
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "EXCHANGE CURRENCY",
            field: "exchangecurrency",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            width: 170,
            valueOptions: filterValues ? filterValues.exchangecurrency : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand
        },
        {
            headerName: "EXCHANGE RATE",
            field: "exchangerate",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 130,
            renderCell: renderCellExpand
        },
        {
            headerName: "DAYS",
            field: "days",
            headerAlign: "center",
            width: 55,
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "SHIPPING COST",
            field: "shippingcost",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 110,
            renderCell: renderCellExpand
        },
        {
            headerName: "FINAL PAYABLE AMOUNT",
            field: "finalpayableamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 170,
            renderCell: renderCellExpand
        },
        {
            headerName: "PDF",
            field: "invoicepdf",
            renderCell: (params) => (
                <BsFilePdf size={25} color="red" onClick={() => downloadPDF(params.row.id, params.row.invoicepdf)} />
            ),
            width: 55,
            export: false,
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "PAID AMOUNT",
            field: "amountpaid",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 110,
            renderCell: renderCellExpand
        },
        {
            headerName: "DATE",
            field: "date",
            type: "date",
            width: 85,
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.row.date),
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "EXPORT",
            field: "export",
            renderCell: (params) =>
                params.row.export ? (
                    <CheckIcon color="success" />
                ) : (
                    <ClearIcon color="error" />
                ),
            headerAlign: "center",
            align: "center",
            width: 65
        },
        {
            headerName: "COUNTRY OF FINAL DESTINATION",
            field: "countryoffinaldestination",
            headerAlign: "center",
            align: "center",
            width: 220,
            renderCell: renderCellExpand
        },
        {
            headerName: "COUNTRY OF ORIGIN",
            field: "countryoforigin",
            headerAlign: "center",
            width: 145,
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "FINAL DESTINATION",
            field: "finaldestination",
            headerAlign: "center",
            align: "center",
            width: 135,
            renderCell: renderCellExpand
        },
        {
            headerName: "ENSURED BY",
            field: "ensuredby",
            headerAlign: "center",
            align: "center",
            width: 95,
            renderCell: renderCellExpand
        },
        {
            headerName: "BANK THROUGH",
            field: "bankthrough",
            renderCell: (params) =>
                params.row.bankthrough ? (
                    <CheckIcon color="success" />
                ) : (
                    <ClearIcon color="error" />
                ),
            headerAlign: "center",
            width: 115,
            align: "center",
        },
        {
            headerName: "PORT OF LANDING",
            field: "portofloading",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand
        },
        {
            headerName: "SHIPPED BY",
            field: "shippedby",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "PORT OF DISCHARGE",
            field: "portofdischarge",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand
        },
        {
            headerName: "REMARKS",
            field: "remarks",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand
        },
        {
            headerName: "TERMS",
            field: "terms",
            headerAlign: "center",
            type: "number",
            width: 60,
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "DUE DATE",
            field: "duedate",
            type: "date",
            width: 85,
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.row.duedate),
            headerAlign: "center",
            align: "center",
        },
    ];

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback(
        (row, mainData) => <DetailPanelContent row={row} mainData={mainData} />,
        [],
    );

    function DetailPanelContent({ row: rowProp, mainData }) {
        let row = mainData.filter(data => data.invoiceid === rowProp.id);

        return (
            <Stack
                sx={{ p: 1, height: '100%', boxSizing: 'border-box', borderBottom: "1px solid lightgrey" }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "59vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto'
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={false}
                        density="compact"
                        columns={columns}
                        rows={row}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '35vh'
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    customercompanyname: false,
                                    id: false
                                },
                            },
                        }}
                        hideFooter
                    />
                </Box>
            </Stack>
        );
    }


    const columns = columnName;
    const defaultMaterialTheme = createTheme();

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                {/* <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} /> */}
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />

                {groupBy && (
                    <>
                        {salesListLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch(fromDate, toDate, user, userOption, location)}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}
                    </>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }

    return (
        <div>
            {
                !access ? <NoPermission /> : (
                    <Box px={1}>
                        <CommonSnackbar
                            show={errorSnackbar}
                            message={Constants.APiFailErrorMessage}
                            variant={"error"}
                            autoHide={Constants.SnackBarCloseTimer}
                            title={"Failed"}
                            handleClose={() => setErrorSnackbar(false)}
                        />
                        {showBucketDetailModal ? (
                            <BucketDetailModal
                                show={showBucketDetailModal}
                                bucketId={bucketId}
                                handleClose={() => setShowBucketDetailModal(false)}
                            />
                        ) : null}
                        {showUserDetailModal ? (
                            <UserDetailModal
                                show={true}
                                handleClose={() => setShowUserDetailModal(false)}
                                handleSelect={(selectedOption) => {
                                    const selectedCustomers = userOption.filter(opt =>
                                        selectedOption.includes(opt.value)
                                    );
                                    setUser(selectedCustomers);
                                    setShowUserDetailModal(false)
                                }}
                                userids={userOption.filter(option => option.value !== "*").map(cust => cust.value)}
                                selectedIds={user.length !== 0 ? user.map(iCustomer => iCustomer.value) : []}
                                usertype={"C"}
                                loading={showUserDetailModal}
                                isMulti={true}
                            />
                        ) : null}

                        {/*need to send pdf in byte string to modal */}
                        <PDFModal show={isModalOpen} onHide={closeModal} pdfByteString={pdfByte} pdfName={pdfName} />

                        {showmodal ? (
                            <InvoicePaymentModal
                                show={showmodal}
                                setShow={() => setShowModal(false)}
                                loader={loader}
                                handleValidation={handleValidation}
                                handleSave={handlePayment}
                                invoiceid={invoiceid}
                                finalamount={finalAmount}
                                currency={currency}
                            />
                        ) : null}

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                width: "100%",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 250px", minWidth: 200, maxWidth: 300, mt: 1 }}>
                                <Typography sx={{ mr: 1, whiteSpace: "nowrap" }}>Location</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Select
                                        menuPlacement="auto"
                                        blurInputOnSelect={false}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        value={location}
                                        isSearchable={true}
                                        name="status"
                                        menuPortalTarget={document.body}
                                        options={locationOptions}
                                        onChange={(selectedOption) => {
                                            setLocation(selectedOption);
                                            setUser([]);
                                            setListData([]);
                                        }}
                                        styles={{ ...Constants.Styles, container: (base) => ({ ...base, maxWidth: 250 }) }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 350px", minWidth: 250, maxWidth: 380, mt: 1 }}>
                                <Typography sx={{ mr: 1, whiteSpace: "nowrap" }}>Customer</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Select
                                        menuPlacement="auto"
                                        blurInputOnSelect={false}
                                        menuPortalTarget={document.body}
                                        styles={{ ...Constants.Styles, container: (base) => ({ ...base, maxWidth: 250 }) }}
                                        closeMenuOnSelect={false}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                        isMulti
                                        options={userOption}
                                        value={user}
                                        onChange={(e) => setUser(e)}
                                    />
                                </Box>
                                <Tooltip title="View More Data">
                                    <span style={{ cursor: "pointer", marginLeft: "8px" }}>
                                        <OpenInNewIcon fontSize="medium" color="action" onClick={() => setShowUserDetailModal(true)} />
                                    </span>
                                </Tooltip>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 250px", minWidth: 200, maxWidth: 300, mt: 1 }}>
                                <Typography sx={{ mr: 1, whiteSpace: "nowrap" }}>Status</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Select
                                        menuPlacement="auto"
                                        blurInputOnSelect={false}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        value={status}
                                        isSearchable={true}
                                        name="status"
                                        menuPortalTarget={document.body}
                                        styles={{ ...Constants.Styles, container: (base) => ({ ...base, maxWidth: 250 }) }}
                                        options={lStatusOption}
                                        onChange={(selectedOption) => setStatus(selectedOption)}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 200px", minWidth: 180, maxWidth: 240, mt: 1 }}>
                                <Typography sx={{ mr: 1 }}>From</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        className="picker"
                                        ampm
                                        renderInput={(props) => (
                                            <TextField
                                                {...props}
                                                size="small"
                                                inputProps={{ ...props.inputProps, readOnly: true }}
                                                fullWidth
                                            />
                                        )}
                                        value={fromDate}
                                        inputFormat="DD/MM/YYYY"
                                        onChange={(newValue) => {
                                            // Get the current time
                                            const now = dayjs();

                                            // Set startOfMonth to the first day of the selected month, but with the current system time
                                            const fromDate = dayjs(newValue)
                                                .hour(now.hour())
                                                .minute(now.minute())
                                                .second(now.second())
                                                .millisecond(now.millisecond())
                                                .toDate();

                                            setFromDate(fromDate);
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 200px", minWidth: 180, maxWidth: 240, mt: 1 }}>
                                <Typography sx={{ mr: 1 }}>To</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        className="picker"
                                        ampm
                                        renderInput={(props) => (
                                            <TextField
                                                {...props}
                                                size="small"
                                                inputProps={{ ...props.inputProps, readOnly: true }}
                                                fullWidth
                                            />
                                        )}
                                        error={false}
                                        value={toDate}
                                        inputFormat="DD/MM/YYYY"
                                        onChange={(newValue) => {
                                            // Get the current time
                                            const now = dayjs();

                                            // Set startOfMonth to the first day of the selected month, but with the current system time
                                            const toDate = dayjs(newValue)
                                                .hour(now.hour())
                                                .minute(now.minute())
                                                .second(now.second())
                                                .millisecond(now.millisecond())
                                                .toDate();

                                            setToDate(toDate);
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Switch checked={groupBy} onChange={() => setGroupBy(!groupBy)} />
                                                <Typography>By Invoice</Typography>
                                            </Box>
                                        }
                                        label=""
                                    />
                                </FormGroup>

                                <LoadingButton
                                    loading={salesListLoader}
                                    loadingPosition="start"
                                    disabled={user.length === 0}
                                    variant="contained"
                                    onClick={() => handleSearch(fromDate, toDate, user, userOption, location)}
                                    startIcon={<SearchOutlinedIcon size="large" />}
                                >
                                    Search
                                </LoadingButton>
                            </Box>
                        </Box>

                        {!groupBy ? (
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "94vw",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "4px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    height: '77vh'
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={false}
                                    density="compact"
                                    columns={columns}
                                    rows={mainData}
                                    getRowId={(row) => row.id}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "8px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "15px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "22px",
                                        },

                                        fontSize: Constants.defaultTableFontSize, fontWeight: 'bold'
                                    }}
                                    slots={{ toolbar: CustomToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                                id: false
                                            },
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                />
                            </Box>
                        ) : (
                            <ThemeProvider theme={defaultMaterialTheme}>
                                <Box
                                    sx={{
                                        backgroundColor: '#fff',
                                        color: '#000',
                                        width: "100%",
                                        position: "relative",
                                        "& .MuiTablePagination-selectLabel": {
                                            marginTop: "15px",
                                        },
                                        "& .MuiTablePagination-displayedRows": {
                                            marginTop: "15px",
                                        },
                                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .css-cc8tf1": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .lavenderCell": {
                                            color: "#080808",
                                            backgroundColor: "#D8BFD8",
                                        },
                                        height: "75vh",
                                    }}
                                >
                                    <StyledDataGrid
                                        rowHeight={65}
                                        disableRowSelectionOnClick={false}
                                        density="compact"
                                        columns={materialDefaultColumns}
                                        rows={listData}
                                        loading={salesListLoader}
                                        sx={{
                                            '& .MuiDataGrid-columnHeader': {
                                                color: '#fff',
                                                backgroundColor: 'grey',
                                            },
                                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                                py: "4px",
                                            },
                                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                                py: "12px",
                                            },
                                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                                py: "18px",
                                            },

                                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                        }}
                                        slots={{ toolbar: CustomToolbar }}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: true,
                                                quickFilterProps: { debounceMs: 500 },
                                            },
                                        }}
                                        sortModel={[
                                            {
                                                field: 'id',
                                                sort: 'desc',
                                            },
                                        ]}
                                        getRowId={(row) => row.id}
                                        initialState={{
                                            pagination: {
                                                paginationModel: Constants.pageSize,
                                            },
                                        }}
                                        getRowClassName={(params) => {
                                            if (params.row.status === 'PAID') {
                                                return 'super-app-theme--Paid';
                                            } else if (params.row.amountpaid > 0) {
                                                return 'super-app-theme--PartiallyPaid';
                                            } else {
                                                return 'super-app-theme--Unpaid';
                                            }
                                        }}
                                        pageSizeOptions={Constants.pageSizeOptions}
                                        pagination
                                        getDetailPanelHeight={getDetailPanelHeight}
                                        getDetailPanelContent={({ row }) => getDetailPanelContent(row, mainData)}
                                    />
                                </Box>
                            </ThemeProvider>
                        )}
                    </Box>
                )}
        </div>
    );
};

export default MixInvoiceAccount;
